import { useEffect, useState } from 'preact/hooks';
import { AppLayout } from '../../components/app/AppLayout';
import { useI18n } from '../../i18n';
import { useGameData } from '../../state/game';
import { FAQ } from '../../lib/faq';
import { localizedName } from '../../lib/static';
import { getTournament, listArticles, type Article, type TournamentInfo } from '../../lib/content';

type Tab = 'faq' | 'stadiums' | 'prizes';

export function Info() {
  const { t } = useI18n();
  const [tab, setTab] = useState<Tab>('faq');
  return (
    <AppLayout>
      <div class="bc-phasetabs">
        {(['faq', 'stadiums', 'prizes'] as Tab[]).map((tk) => (
          <button key={tk} class={`bc-pill${tab === tk ? ' is-active' : ''}`} onClick={() => setTab(tk)}>
            {t(`info.tabs.${tk}`)}
          </button>
        ))}
      </div>
      {tab === 'faq' && <FaqTab />}
      {tab === 'stadiums' && <StadiumsTab />}
      {tab === 'prizes' && <PrizesTab />}
    </AppLayout>
  );
}

function FaqTab() {
  const { locale } = useI18n();
  const [open, setOpen] = useState<number | null>(0);
  return (
    <div class="faq">
      {FAQ.map((item, i) => (
        <div key={i} class={`faq__item${open === i ? ' is-open' : ''}`}>
          <button class="faq__q" onClick={() => setOpen(open === i ? null : i)}>
            {localizedName(item.q, locale)}
            <span class="faq__chev">{open === i ? '–' : '+'}</span>
          </button>
          {open === i && <p class="faq__a">{localizedName(item.a, locale)}</p>}
        </div>
      ))}
    </div>
  );
}

function StadiumsTab() {
  const { locale } = useI18n();
  const { bundle } = useGameData();
  if (!bundle) return <div class="bc-spinner">…</div>;
  const stadiums = Object.values(bundle.stadiums).sort((a, b) => a.city.localeCompare(b.city));
  return (
    <div class="stadiums">
      {stadiums.map((s) => (
        <div key={s.code} class="bc-card stadium">
          {s.poster && <img class="stadium__poster" src={`/stadiums/${s.poster}`} alt={s.name} loading="lazy" />}
          <div class="stadium__body">
            <strong>{s.name}</strong>
            <span class="stadium__meta">
              {s.city}{s.capacity ? ` · ${s.capacity.toLocaleString(locale)}` : ''}
            </span>
            {s.curiosities?.[locale]?.[0] && <p class="stadium__cur">{s.curiosities[locale][0]}</p>}
          </div>
        </div>
      ))}
    </div>
  );
}

function PrizesTab() {
  const { t, locale } = useI18n();
  const [info, setInfo] = useState<TournamentInfo | null>(null);
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getTournament().catch(() => null), listArticles().catch(() => [])])
      .then(([i, a]) => {
        setInfo(i);
        setArticles(a);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div class="bc-spinner">{t('app.loading')}</div>;

  return (
    <div class="prizes">
      {info?.prizeCounterVisible && info.prizeLitres != null && (
        <div class="bc-card prizes__pool">
          <span class="prizes__litres">{info.prizeLitres.toLocaleString(locale)}</span>
          <span class="prizes__litreslbl">{t('info.litres_pool')}</span>
        </div>
      )}
      {articles.length === 0 ? (
        <p class="bc-empty">{t('info.no_prizes')}</p>
      ) : (
        <div class="prizes__grid">
          {articles.map((a) => (
            <div key={a.id} class="bc-card prize">
              {a.imagePath && <img class="prize__img" src={a.imagePath} alt={a.title} loading="lazy" />}
              <strong>{a.title}</strong>
              {a.partnerName && <span class="prize__partner">{a.partnerName}</span>}
              {a.valueChf != null && <span class="prize__value">CHF {a.valueChf}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
